import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchCoachings } from '../store/slices/coachingsSlice';
import useAutoRefresh from '../hooks/useAutoRefresh';
import { Plus, Search, Eye, RefreshCw, AlertTriangle, FileSpreadsheet, FileText, ChevronDown, Download } from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-toastify';

/* ── Modal Renouveler (inline — évite la navigation vers la fiche) ── */
const DOMAINES_LABEL = {
  action: 'ACTION', obligation: 'OBLIGATION', autre: 'AUTRE',
};
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

function ModalRenouveler({ coaching, onClose, onSuccess }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    nouvelle_duree_mois: '',
    nouveau_prix:        String(coaching?.prix || ''),
    montant_paye:        '0',
    statut_paiement:     'impaye',
    observation:         '',
  });
  const [submitting,   setSubmitting]   = useState(false);
  const [dateFinCalc,  setDateFinCalc]  = useState('');

  useEffect(() => {
    if (coaching?.date_fin && form.nouvelle_duree_mois > 0) {
      const d = new Date(coaching.date_fin);
      d.setMonth(d.getMonth() + parseInt(form.nouvelle_duree_mois));
      setDateFinCalc(d.toLocaleDateString('fr-FR'));
    } else setDateFinCalc('');
  }, [coaching?.date_fin, form.nouvelle_duree_mois]);

  const handle = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nouvelle_duree_mois || parseInt(form.nouvelle_duree_mois) < 1) {
      toast.error('La durée est obligatoire (min. 1 mois).'); return;
    }
    if (!form.nouveau_prix || parseInt(form.nouveau_prix) < 1) {
      toast.error('Le nouveau prix est obligatoire.'); return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/coachings/${coaching.id}/renouveler/`, {
        nouvelle_duree_mois: parseInt(form.nouvelle_duree_mois),
        nouveau_prix:        parseInt(form.nouveau_prix),
        montant_paye:        parseInt(form.montant_paye) || 0,
        statut_paiement:     form.statut_paiement,
        observation:         form.observation,
      });
      toast.success('Contrat renouvelé — nouveau contrat créé.');
      onSuccess();
      navigate(`/coachings/${data.nouveau_coaching_id}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors du renouvellement.');
    } finally { setSubmitting(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: '100%', maxWidth: 520, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <RefreshCw size={20} color="#1F4E79" />
          <h2 style={{ fontSize: 17, fontWeight: 700, color: '#1F4E79', margin: 0 }}>Renouveler le contrat</h2>
        </div>

        <div style={{ background: '#EBF3FB', borderRadius: 8, padding: '10px 14px', marginBottom: 18, fontSize: 13, color: '#1F4E79' }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Contrat #{coaching?.id} — {coaching?.client_nom}</div>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 12 }}>
            <span><strong>Fin :</strong> {fmtDate(coaching?.date_fin)}</span>
            <span><strong>Prix :</strong> {parseInt(coaching?.prix || 0).toLocaleString('fr-FR')} FCFA</span>
            <span><strong>Produits :</strong> {DOMAINES_LABEL[coaching?.domaine] || coaching?.domaine}</span>
          </div>
          <div style={{ marginTop: 6, fontSize: 11, color: '#C00000', fontWeight: 600 }}>
            Ce contrat passera en « Expiré » — un nouveau contrat sera créé automatiquement.
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="form-group">
              <label className="form-label">Nouvelle durée (mois) <span style={{ color: '#C00000' }}>*</span></label>
              <input type="number" min="1" className="form-control" name="nouvelle_duree_mois"
                value={form.nouvelle_duree_mois} onChange={handle} placeholder="Ex : 3" />
            </div>
            <div className="form-group">
              <label className="form-label">Nouveau prix (FCFA) <span style={{ color: '#C00000' }}>*</span></label>
              <input type="number" min="1" className="form-control" name="nouveau_prix"
                value={form.nouveau_prix} onChange={handle} />
            </div>
            <div className="form-group">
              <label className="form-label">Montant déjà payé (FCFA)</label>
              <input type="number" min="0" className="form-control" name="montant_paye"
                value={form.montant_paye} onChange={handle} />
            </div>
            <div className="form-group">
              <label className="form-label">Statut paiement</label>
              <select className="form-control" name="statut_paiement" value={form.statut_paiement} onChange={handle}>
                <option value="impaye">Impayé</option>
                <option value="partiel">Partiel</option>
                <option value="paye">Payé</option>
              </select>
            </div>
          </div>

          {dateFinCalc && (
            <div style={{ background: '#E8F5E9', border: '1px solid #A5D6A7', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#1E7145', fontWeight: 600 }}>
              Nouveau contrat : du {fmtDate(coaching?.date_fin)} au {dateFinCalc}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Observations</label>
            <textarea className="form-control" rows={2} name="observation"
              value={form.observation} onChange={handle}
              placeholder="Conditions du renouvellement…" style={{ resize: 'vertical' }} />
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <button type="button" className="header-btn btn-secondary" onClick={onClose} style={{ padding: '9px 20px', fontSize: 14 }}>
              Annuler
            </button>
            <button type="submit" className="header-btn btn-success" disabled={submitting}
              style={{ padding: '9px 24px', fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <RefreshCw size={15} />
              {submitting ? 'Création...' : 'Créer le nouveau contrat'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Constantes ── */
const DOMAINES = {
  action:     'ACTION',
  obligation: 'OBLIGATION',
  autre:      'AUTRE',
};
const DOMAINES_COURT = {
  action:     'ACTION',
  obligation: 'OBLIG.',
  autre:      'AUTRE',
};
const STATUTS = {
  en_cours: { label: 'En cours', badge: 'vert'  },
  expire:   { label: 'Expiré',   badge: 'gris'  },
  resilie:  { label: 'Résilié',  badge: 'rouge' },
};
const PAIEMENTS = {
  paye:    { label: 'Payé',    badge: 'vert'   },
  partiel: { label: 'Partiel', badge: 'orange' },
  impaye:  { label: 'Impayé',  badge: 'rouge'  },
};
const SGI_LABELS = {
  sgi_bs:                 'SGI BS',
  sgi_pcm:                'SGI PCM',
  nsia_finance:           'NSIA FINANCE',
  sgi_bici_bourse:        'SGI BICI BOURSE',
  societe_generale:       'SOCIÉTÉ GÉNÉRALE',
  atlantique_finance:     'ATLANTIQUE FINANCE',
  boa_capital_securities: 'BOA CAPITAL SECURITIES',
  autre:                  'Autre',
};

export default function CoachingsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { liste, loading } = useSelector(s => s.coachings);

  const [search,          setSearch]          = useState('');
  const [filterStatuts,   setFilterStatuts]   = useState(() => new Set());
  const [filterPaiements, setFilterPaiements] = useState(() => new Set());
  const [exportMenu,      setExportMenu]      = useState(false);
  const [exporting,     setExporting]     = useState(false);
  const [renewCoaching, setRenewCoaching] = useState(null); // coaching à renouveler

  /* Chargement unique — le filtrage est côté client */
  useEffect(() => { dispatch(fetchCoachings()); }, [dispatch]);
  useAutoRefresh(fetchCoachings, 60000);

  const toggleStatut   = (v) => setFilterStatuts(s   => { const n = new Set(s); n.has(v) ? n.delete(v) : n.add(v); return n; });
  const togglePaiement = (v) => setFilterPaiements(s => { const n = new Set(s); n.has(v) ? n.delete(v) : n.add(v); return n; });

  /* ── Filtrage client-side ── */
  const filtered = (liste || []).filter(c => {
    if (search) {
      const q = search.toLowerCase();
      const domLabel = (DOMAINES[c.domaine] || c.domaine || '').toLowerCase();
      const sgiLabel = (SGI_LABELS[c.sgi]   || c.sgi    || '').toLowerCase();
      const matches  =
        (c.client_nom       || '').toLowerCase().includes(q) ||
        (c.client_telephone || '').includes(q) ||
        domLabel.includes(q) || sgiLabel.includes(q);
      if (!matches) return false;
    }
    if (filterStatuts.size   > 0 && !filterStatuts.has(c.statut))            return false;
    if (filterPaiements.size > 0 && !filterPaiements.has(c.statut_paiement)) return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => new Date(a.date_fin) - new Date(b.date_fin));

  /* ── Export (envoie les filtres actifs au backend pour coller à la vue) ── */
  const exportParams = () => {
    const p = {};
    if (filterStatuts.size   === 1) p.statut          = [...filterStatuts][0];
    if (filterPaiements.size === 1) p.statut_paiement = [...filterPaiements][0];
    if (search)                     p.search          = search;
    return p;
  };

  const exportCSV = () => {
    setExportMenu(false);
    const headers = ['Client', 'Téléphone', 'Type', 'Produits', 'SGI', 'Prix (FCFA)', 'Montant payé (FCFA)',
      'Statut paiement', 'Date début', 'Date fin', 'Durée (mois)', 'Statut', 'Contrat N°'];
    const rows = sorted.map(c => [
      c.client_nom, c.client_telephone || '',
      c.type_coaching === 'individuel' ? 'Individuel' : 'Groupe',
      DOMAINES[c.domaine] || c.domaine,
      SGI_LABELS[c.sgi] || c.sgi || '',
      parseInt(c.prix || 0),
      parseInt(c.montant_paye || 0),
      PAIEMENTS[c.statut_paiement]?.label || c.statut_paiement,
      c.date_debut ? new Date(c.date_debut).toLocaleDateString('fr-FR') : '',
      c.date_fin   ? new Date(c.date_fin).toLocaleDateString('fr-FR')   : '',
      c.duree_mois,
      STATUTS[c.statut]?.label || c.statut,
      (c.numero_renouvellement ?? 0) + 1,
    ]);
    const csv = [headers, ...rows]
      .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';'))
      .join('\r\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `coachings_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${sorted.length} coaching(s) exporté(s) en CSV.`);
  };

  const handleExport = async (format) => {
    setExporting(true);
    setExportMenu(false);
    try {
      const endpoint = format === 'excel' ? '/coachings/export/excel/' : '/coachings/export/pdf/';
      const response = await api.get(endpoint, { params: exportParams(), responseType: 'blob' });
      const mime = format === 'excel'
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'application/pdf';
      const ext  = format === 'excel' ? 'xlsx' : 'pdf';
      const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const url  = URL.createObjectURL(new Blob([response.data], { type: mime }));
      const a    = document.createElement('a');
      a.href = url; a.download = `coachings_${date}.${ext}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Export ${format.toUpperCase()} téléchargé.`);
    } catch { toast.error("Erreur lors de l'export. Réessayez."); }
    finally  { setExporting(false); }
  };

  /* ── Helpers ── */
  const urgenceBadge = (c) => {
    const j = c.jours_restants;
    if (j === null || j === undefined) return null;
    if (j < 0)   return <span className="badge badge-rouge"><AlertTriangle size={10} style={{ marginRight: 3 }}/>Échu</span>;
    if (j <= 15) return <span className="badge badge-rouge">J-{j}</span>;
    if (j <= 60) return <span className="badge badge-orange">J-{j}</span>;
    return <span className="badge badge-vert">J-{j}</span>;
  };

  const pctPaiement = (c) => {
    if (!c.prix || c.prix === '0') return 0;
    return Math.min(100, Math.round((parseFloat(c.montant_paye) / parseFloat(c.prix)) * 100));
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

  /* KPIs calculés sur la liste filtrée */
  const nbEnCours  = filtered.filter(c => c.statut === 'en_cours').length;
  const nbImpayes  = filtered.filter(c => c.statut_paiement === 'impaye' && c.statut === 'en_cours').length;
  const nbCritique = filtered.filter(c => c.est_critique).length;
  const totalCA    = filtered.reduce((s, c) => s + parseFloat(c.montant_paye || 0), 0);

  return (
    <div className="page-list">
      {/* ══════════════════════════════════════
          BARRE UNIQUE : titre + filtres + boutons
      ══════════════════════════════════════ */}
      <div style={{ marginBottom: 18 }}>

        {/* Barre horizontale : recherche + filtres + boutons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>

          {/* ── Recherche universelle ── */}
          <div className="search-bar" style={{ flex: '1 1 200px', minWidth: 200 }}>
            <Search size={14} />
            <input
              placeholder="Client, domaine, SGI, statut, paiement…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ fontSize: 12 }}
            />
          </div>

          {/* ── Séparateur ── */}
          <div style={{ width: 1, height: 28, background: '#E5E7EB', flexShrink: 0 }} />

          {/* ── Export ── */}
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <button
              className="header-btn btn-secondary"
              onClick={() => setExportMenu(v => !v)}
              disabled={exporting}
              style={{ height: 36, padding: '0 12px', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5 }}
            >
              {exporting ? 'Export…' : 'Exporter'}
              <ChevronDown size={13} />
            </button>
            {exportMenu && (
              <>
                <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setExportMenu(false)} />
                <div style={{
                  position: 'absolute', top: '100%', right: 0, marginTop: 4,
                  background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10,
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 100,
                  minWidth: 210, overflow: 'hidden',
                }}>
                  <div style={{ padding: '7px 12px', fontSize: 10, color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', borderBottom: '1px solid #F3F4F6' }}>
                    Exporter la sélection ({filtered.length})
                  </div>
                  {[
                    { fn: () => exportCSV(),          Icon: Download,        color: '#1F4E79', label: 'Exporter en CSV',   sub: 'Fichier .csv — ouvre dans Excel' },
                    { fn: () => handleExport('excel'), Icon: FileSpreadsheet, color: '#1E7145', label: 'Exporter en Excel', sub: 'Fichier .xlsx avec mise en forme' },
                    { fn: () => handleExport('pdf'),   Icon: FileText,        color: '#C00000', label: 'Exporter en PDF',   sub: 'Document paysage prêt à imprimer' },
                  ].map(({ fn, Icon, color, label, sub }, idx) => (
                    <div key={label}>
                      {idx > 0 && <div style={{ height: 1, background: '#F3F4F6' }} />}
                      <button
                        onClick={fn}
                        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, color: '#1F2937', textAlign: 'left' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      >
                        <Icon size={16} color={color} />
                        <div>
                          <div style={{ fontWeight: 600 }}>{label}</div>
                          <div style={{ fontSize: 11, color: '#6B7280' }}>{sub}</div>
                        </div>
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* ── Nouveau coaching ── */}
          <button
            className="header-btn btn-primary"
            onClick={() => navigate('/coachings/nouveau')}
            style={{ height: 36, padding: '0 14px', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
          >
            <Plus size={15} /> Nouveau coaching
          </button>
        </div>
        {/* Ligne 2 : filtres checkboxes */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
          padding: '6px 12px', background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, marginTop: 8 }}>

          {/* Statut */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 11, color: '#6B7280', fontWeight: 600, flexShrink: 0 }}>Statut :</span>
            {[
              { value: 'en_cours', label: 'En cours', color: '#1E7145' },
              { value: 'expire',   label: 'Expiré',   color: '#6B7280' },
              { value: 'resilie',  label: 'Résilié',  color: '#C00000' },
            ].map(({ value, label, color }) => (
              <label key={value} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer',
                fontSize: 12, fontWeight: filterStatuts.has(value) ? 700 : 400,
                color: filterStatuts.has(value) ? color : '#6B7280', userSelect: 'none' }}>
                <input type="checkbox" checked={filterStatuts.has(value)} onChange={() => toggleStatut(value)}
                  style={{ accentColor: color, cursor: 'pointer', width: 14, height: 14 }} />
                {label}
              </label>
            ))}
          </div>

          <div style={{ width: 1, height: 20, background: '#E5E7EB', flexShrink: 0 }} />

          {/* Paiement */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 11, color: '#6B7280', fontWeight: 600, flexShrink: 0 }}>Paiement :</span>
            {[
              { value: 'paye',    label: 'Payé',    color: '#1E7145' },
              { value: 'partiel', label: 'Partiel', color: '#ED7D31' },
              { value: 'impaye',  label: 'Impayé',  color: '#C00000' },
            ].map(({ value, label, color }) => (
              <label key={value} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer',
                fontSize: 12, fontWeight: filterPaiements.has(value) ? 700 : 400,
                color: filterPaiements.has(value) ? color : '#6B7280', userSelect: 'none' }}>
                <input type="checkbox" checked={filterPaiements.has(value)} onChange={() => togglePaiement(value)}
                  style={{ accentColor: color, cursor: 'pointer', width: 14, height: 14 }} />
                {label}
              </label>
            ))}
          </div>

          {(filterStatuts.size > 0 || filterPaiements.size > 0) && (
            <button onClick={() => { setFilterStatuts(new Set()); setFilterPaiements(new Set()); }}
              style={{ fontSize: 11, color: '#C00000', background: 'none', border: 'none',
                cursor: 'pointer', textDecoration: 'underline', padding: 0, marginLeft: 'auto' }}>
              Tout afficher
            </button>
          )}
        </div>
      </div>

      {/* ── KPIs (sur la sélection filtrée) ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 16 }}>
        {[
          { label: 'En cours',        value: nbEnCours,  color: '#1E7145', bg: '#E8F5E9' },
          { label: 'Échéances crit.', value: nbCritique, color: '#C00000', bg: '#FCE4EC' },
          { label: 'Impayés actifs',  value: nbImpayes,  color: '#ED7D31', bg: '#FFF8E7' },
          { label: 'CA encaissé',     value: `${totalCA.toLocaleString('fr-FR')} F`, color: '#1F4E79', bg: '#EBF3FB' },
        ].map(k => (
          <div key={k.label} className="card" style={{ padding: '10px 16px', borderLeft: `4px solid ${k.color}`, background: k.bg }}>
            <p style={{ fontSize: 10, color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', margin: 0 }}>{k.label}</p>
            <p style={{ fontSize: 20, fontWeight: 700, color: k.color, margin: '2px 0 0' }}>{k.value}</p>
          </div>
        ))}
      </div>

      {/* ── Tableau ── */}
      <div className="table-container">
        {loading ? (
          <div className="spinner"><div className="spinner-ring" /> Chargement...</div>
        ) : (
          <table style={{ width: '100%', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: 34   }} /> {/* N° */}
              <col style={{ width: '22%' }} /> {/* Client */}
              <col style={{ width: 52   }} /> {/* Type */}
              <col style={{ width: '16%' }} /> {/* Domaine / SGI */}
              <col style={{ width: '14%' }} /> {/* Prix / Payé */}
              <col style={{ width: 88   }} /> {/* Avancement */}
              <col style={{ width: 110  }} /> {/* Période */}
              <col style={{ width: 60   }} /> {/* Urgence */}
              <col style={{ width: 108  }} /> {/* Statut / Paiement */}
              <col style={{ width: 62   }} /> {/* Actions */}
            </colgroup>
            <thead>
              <tr>
                <th style={{ fontSize: 11 }}>N°</th>
                <th style={{ fontSize: 11 }}>Client</th>
                <th style={{ fontSize: 11 }}>Type</th>
                <th style={{ fontSize: 11 }}>Produits / SGI</th>
                <th style={{ fontSize: 11 }}>Prix / Payé</th>
                <th style={{ fontSize: 11 }}>Avancement</th>
                <th style={{ fontSize: 11 }}>Période</th>
                <th style={{ fontSize: 11 }}>Urgence</th>
                <th style={{ fontSize: 11 }}>Statut / Paiement</th>
                <th style={{ fontSize: 11 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ textAlign: 'center', padding: 32, color: '#6B7280' }}>
                    {search || filterStatuts.size > 0 || filterPaiements.size > 0
                      ? 'Aucun résultat pour ce filtre'
                      : 'Aucun coaching trouvé'}
                  </td>
                </tr>
              ) : sorted.map((c, i) => {
                const pct = pctPaiement(c);
                const s   = STATUTS[c.statut]              || { label: c.statut,           badge: 'gris' };
                const p   = PAIEMENTS[c.statut_paiement]   || { label: c.statut_paiement,  badge: 'gris' };
                return (
                  <tr key={c.id} style={c.est_critique ? { background: '#FFF5F5' } : {}}>

                    <td style={{ color: '#1F4E79', fontSize: 11, fontWeight: 700, padding: '8px 6px' }}>{i + 1}</td>

                    <td style={{ padding: '8px 6px', overflow: 'hidden' }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#1F2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {c.client_nom}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 2, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 10, color: '#6B7280' }}>{c.client_telephone}</span>
                        {c.numero_renouvellement > 0
                          ? <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4, background: '#EDE9FE', color: '#5B21B6' }}>
                              Renouvellement #{c.numero_renouvellement}
                            </span>
                          : <span style={{ fontSize: 9, fontWeight: 600, padding: '1px 5px', borderRadius: 4, background: '#E8F5E9', color: '#1E7145' }}>
                              1er contrat
                            </span>
                        }
                      </div>
                    </td>

                    <td style={{ padding: '8px 4px', textAlign: 'center' }}>
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '2px 5px', borderRadius: 4,
                        background: c.type_coaching === 'individuel' ? '#EBF3FB' : '#EDE9FE',
                        color:      c.type_coaching === 'individuel' ? '#1F4E79' : '#5B21B6',
                      }}>
                        {c.type_coaching === 'individuel' ? 'Indiv.' : 'Groupe'}
                      </span>
                    </td>

                    <td style={{ padding: '8px 6px', overflow: 'hidden' }}>
                      <div style={{ fontSize: 11, color: '#1F2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {DOMAINES_COURT[c.domaine] || c.domaine}
                      </div>
                      {c.sgi
                        ? <span className="badge badge-bleu" style={{ fontSize: 10, marginTop: 2, display: 'inline-block' }}>{SGI_LABELS[c.sgi] || c.sgi}</span>
                        : <span style={{ fontSize: 10, color: '#9CA3AF' }}>—</span>}
                    </td>

                    <td style={{ padding: '8px 6px' }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: '#1F4E79' }}>
                        {parseInt(c.prix || 0).toLocaleString('fr-FR')} F
                      </div>
                      <div style={{ fontSize: 10, color: '#6B7280' }}>
                        {parseInt(c.montant_paye || 0).toLocaleString('fr-FR')} F
                      </div>
                    </td>

                    <td style={{ padding: '8px 6px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <div style={{ flex: 1, height: 5, background: '#E5E7EB', borderRadius: 3 }}>
                          <div style={{
                            width: `${pct}%`, height: '100%', borderRadius: 3,
                            background: pct >= 100 ? '#1E7145' : pct >= 50 ? '#ED7D31' : '#C00000',
                          }} />
                        </div>
                        <span style={{ fontSize: 10, color: '#6B7280', minWidth: 24 }}>{pct}%</span>
                      </div>
                    </td>

                    <td style={{ padding: '8px 6px' }}>
                      <div style={{ fontSize: 10, color: '#6B7280' }}>{fmt(c.date_debut)}</div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#1F2937' }}>→ {fmt(c.date_fin)}</div>
                    </td>

                    <td style={{ padding: '8px 4px', textAlign: 'center' }}>{urgenceBadge(c)}</td>

                    <td style={{ padding: '8px 4px' }}>
                      <div><span className={`badge badge-${s.badge}`} style={{ fontSize: 10 }}>{s.label}</span></div>
                      <div style={{ marginTop: 3 }}><span className={`badge badge-${p.badge}`} style={{ fontSize: 10 }}>{p.label}</span></div>
                    </td>

                    <td style={{ padding: '8px 4px' }}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="header-btn btn-secondary btn-sm btn-icon"
                          onClick={() => navigate(`/coachings/${c.id}`)} title="Voir la fiche">
                          <Eye size={13} />
                        </button>
                        {c.statut === 'en_cours' && (
                          <button className="header-btn btn-success btn-sm btn-icon"
                            onClick={() => setRenewCoaching(c)} title="Renouveler">
                            <RefreshCw size={13} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {renewCoaching && (
        <ModalRenouveler
          coaching={renewCoaching}
          onClose={() => setRenewCoaching(null)}
          onSuccess={() => { setRenewCoaching(null); dispatch(fetchCoachings()); }}
        />
      )}
    </div>
  );
}
